import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import initViewer from '../initViewer';
import '../styles.css';
import logger from '../util/logger.js';

/**
 * ExampleComponent is an example component.
 * It takes a property, `label`, and
 * displays it.
 * It renders an input with the property `value`
 * which is editable by the user.
 */
const GlbViewer = ({ id, sensorsYamlUrl = null, sensors = null, sensorValues = null, modelsPath = '/', modelId = null, width = '100%', height = '400px', options = null, fitTrigger = 0, enableSensorEditor = false, setProps, persistence = false, persistence_type = null, persisted_props = null, cameraState = null, persistentLabels = null }) => {
    const containerRef = useRef(null);
    const viewerRef = useRef(null);
    const prevModelLoadedRef = useRef(null);
    const pendingSensorValuesRef = useRef(null);
    const cameraPollRef = useRef(null);
    const lastCameraStateRef = useRef(null);
    const persistentLabelsRef = useRef(new Set());

    useEffect(() => {
        const mountedRef = { current: true };
        logger.debug && logger.debug('GlbViewer: mounting initViewer');
        (async () => {
            if (!containerRef.current) {
                logger.debug && logger.debug('GlbViewer: mount aborted — containerRef missing');
                return;
            }
            // hover events will be debounced before forwarding to Dash
            const hoverDebounceRef = { timer: null };
            function forwardHover(payload) {
                try {
                    if (typeof setProps === 'function') {
                        setProps({ hoveredSensor: payload });
                    }
                } catch (e) { logger.warn && logger.warn('GlbViewer: forwardHover failed', e); }
            }

            const initOptions = options || {};

            const api = await initViewer(containerRef.current, {
                sensorsYamlUrl,
                sensorsData: sensors,
                modelsPath,
                modelId,
                options: initOptions,
                // forward readiness so host component may flush pending updates
                onReady: () => {
                    try {
                        // no-op for now; React wrapper will register onReady on viewerRef assignment
                    } catch (e) { }
                },
                onModelLoaded: (mid) => {
                    if (!mountedRef.current) {
                        logger.debug && logger.debug('GlbViewer: onModelLoaded ignored — component unmounted', mid);
                        return;
                    }
                    try {
                        if (typeof setProps === 'function') {
                            if (prevModelLoadedRef.current !== mid) {
                                prevModelLoadedRef.current = mid;
                                setProps({ modelLoaded: mid });
                            }
                        }
                    } catch (e) { logger.warn && logger.warn('GlbViewer: onModelLoaded setProps failed', e); }
                },
                onSensorClick: (payload) => {
                    try {
                        if (!mountedRef.current) return;
                        if (typeof setProps === 'function') setProps({ lastSensorClicked: payload });
                    } catch (e) { logger.warn && logger.warn('GlbViewer: onSensorClick setProps failed', e); }
                },
                onHover: (payload) => {
                    try {
                        if (!mountedRef.current) return;
                        // debounce hover updates to avoid flooding Dash callbacks
                        if (hoverDebounceRef.timer) clearTimeout(hoverDebounceRef.timer);
                        hoverDebounceRef.timer = setTimeout(() => {
                            forwardHover(payload);
                            hoverDebounceRef.timer = null;
                        }, 100);
                    } catch (e) { logger.warn && logger.warn('GlbViewer: onHover handler failed', e); }
                },
                // click reporting disabled to avoid pointer-down crash; click events are
                // still handled internally by the viewer but are not reported to Dash.
            });
            console.log('GlbViewer: initViewer returned', { api });
            viewerRef.current = api;
            // If any sensorValues were stored while mounting, flush them
            try {
                if (pendingSensorValuesRef.current && typeof viewerRef.current.updateSensorValues === 'function') {
                    try { viewerRef.current.updateSensorValues(pendingSensorValuesRef.current); } catch (e) { }
                    pendingSensorValuesRef.current = null;
                }
            } catch (e) { }
            // If host provided persisted cameraState, apply immediately
            try {
                if (cameraState && viewerRef.current) {
                    try {
                        // Tell the underlying viewer to skip the automatic fit
                        // that runs after model load — we are restoring an
                        // explicit camera state and do not want an immediate fit.
                        try { viewerRef.current._skipNextFit = true; } catch (e) { }
                        const cam = viewerRef.current._getCamera && viewerRef.current._getCamera();
                        if (cam) {
                            if (cameraState.position) cam.position.set(cameraState.position.x || 0, cameraState.position.y || 0, cameraState.position.z || 0);
                            if (cameraState.quaternion) cam.quaternion.set(cameraState.quaternion.x || 0, cameraState.quaternion.y || 0, cameraState.quaternion.z || 0, cameraState.quaternion.w || 1);
                            try { if (cam.updateMatrixWorld) cam.updateMatrixWorld(); } catch (e) { }
                        }
                    } catch (e) { }
                }
            } catch (e) { }

            // If host provided persisted label list, dispatch label:persistent to restore
            try {
                // Try prop-based restore first (host-provided persistedLabels).
                const parent = containerRef && containerRef.current ? containerRef.current : null;
                let restored = false;
                try {
                    if (Array.isArray(persistentLabels) && persistentLabels.length && viewerRef.current) {
                        if (parent) {
                            logger.debug && logger.debug('GlbViewer: restoring persistentLabels (from prop)', persistentLabels);
                            for (let uid of persistentLabels) {
                                try { parent.dispatchEvent(new CustomEvent('label:persistent', { detail: { sensorUid: uid, persistent: true } })); } catch (e) { logger.warn && logger.warn('GlbViewer: dispatch label:persistent failed', e); }
                            }
                        }
                        restored = true;
                    }
                } catch (e) { logger.warn && logger.warn('GlbViewer: error restoring persistentLabels from prop', e); }

                // Fallback: if host didn't persist labels, try localStorage by component id
                try {
                    if (!restored && typeof id === 'string' && viewerRef.current && parent) {
                        const key = `glb-viewer-persistentLabels-${id}`;
                        const stored = (typeof localStorage !== 'undefined') ? localStorage.getItem(key) : null;
                        if (stored) {
                            try {
                                const arr = JSON.parse(stored);
                                if (Array.isArray(arr) && arr.length) {
                                    logger.debug && logger.debug('GlbViewer: restoring persistentLabels (from localStorage)', arr);
                                    for (let uid of arr) {
                                        try { parent.dispatchEvent(new CustomEvent('label:persistent', { detail: { sensorUid: uid, persistent: true } })); } catch (e) { logger.warn && logger.warn('GlbViewer: dispatch label:persistent failed', e); }
                                    }
                                    restored = true;
                                }
                            } catch (e) { logger.warn && logger.warn('GlbViewer: failed to parse localStorage persistentLabels', e); }
                        }
                    }
                } catch (e) { /* ignore localStorage access errors */ }
            } catch (e) { }
        })();

        return () => {
            mountedRef.current = false;
            logger.debug && logger.debug('GlbViewer: disposing viewer');
            try { if (viewerRef.current && typeof viewerRef.current.dispose === 'function') viewerRef.current.dispose(); } catch (e) { logger.warn('viewer dispose failed', e); }
            viewerRef.current = null;
        };
        // remove `sensors` from the main mount deps to avoid remounts when parent passes
        // a new array/object identity; watch sensors separately.
    }, [sensorsYamlUrl, modelsPath, modelId, options]);

    // optional sensor editor UI (lazy loaded) — controlled via prop `enableSensorEditor`
    const sensorEditorCleanupRef = useRef(null);
    useEffect(() => {
        (async () => {
            try {
                if (enableSensorEditor && viewerRef.current) {
                    const mod = await import('../ui/sensorEditor.js');
                    if (mod && typeof mod.default === 'function') {
                        // init returns cleanup
                        sensorEditorCleanupRef.current = mod.default(viewerRef.current);
                    }
                } else {
                    if (sensorEditorCleanupRef.current && typeof sensorEditorCleanupRef.current === 'function') {
                        try { sensorEditorCleanupRef.current(); } catch (e) { logger.warn && logger.warn('GlbViewer: sensorEditor cleanup failed', e); }
                        sensorEditorCleanupRef.current = null;
                    }
                }
            } catch (e) { logger.warn && logger.warn('GlbViewer: sensorEditor init failed', e); }
        })();
        return () => { if (sensorEditorCleanupRef.current) { try { sensorEditorCleanupRef.current(); } catch (e) { } sensorEditorCleanupRef.current = null; } };
    }, [enableSensorEditor, viewerRef.current]);

    // external trigger to refit the camera to the scene; increment `fitTrigger` to request
    useEffect(() => {
        try {
            if (viewerRef.current && typeof viewerRef.current.fitToScene === 'function') {
                viewerRef.current.fitToScene();
            }
        } catch (e) { logger.warn && logger.warn('GlbViewer: fitToScene failed', e); }
    }, [fitTrigger]);

    // Watch `sensors` changes separately and load new sensors without remounting.
    const prevSensorsRef = useRef(null);
    useEffect(() => {
        try {
            const prev = prevSensorsRef.current;
            // simple deep compare via JSON stringify (sufficient for small sensor lists)
            const a = prev === null ? null : JSON.stringify(prev);
            let b = null;
            try { b = sensors === null || sensors === undefined ? null : JSON.stringify(sensors); } catch (e) { b = String(sensors); }
            if (a !== b) {
                prevSensorsRef.current = sensors;
                logger.debug && logger.debug('GlbViewer: sensors prop changed — reloading sensors', { prev: prev, next: sensors });
                try {
                    if (viewerRef.current && typeof viewerRef.current.load === 'function') {
                        viewerRef.current.load({ sensorsData: sensors });
                    }
                } catch (e) { logger.warn && logger.warn('GlbViewer: failed to load new sensors', e); }
            }
        } catch (e) { logger.warn && logger.warn('GlbViewer: sensors watch error', e); }
    }, [sensors]);

    // Watch `sensorValues` prop and apply incremental updates when provided
    useEffect(() => {
        try {
            logger.debug && logger.debug('GlbViewer: sensorValues prop changed', sensorValues);
            // Extra console logs to aid debugging when logger may be disabled
            try { console.log('GlbViewer: sensorValues effect fired', { sensorValues }); } catch (e) { }
            if (!sensorValues) return;

            // If viewer not yet initialized, store latest values to flush on ready
            if (!viewerRef.current) {
                try { pendingSensorValuesRef.current = sensorValues; } catch (e) { }
                return;
            }

            const hasUpdate = typeof viewerRef.current.updateSensorValues === 'function';
            const hasLoad = typeof viewerRef.current.load === 'function';
            try { console.log('GlbViewer: viewer API presence', { hasUpdate, hasLoad, viewerRefCurrent: !!viewerRef.current }); } catch (e) { }

            if (hasUpdate) {
                try {
                    console.log('GlbViewer: calling viewer.updateSensorValues');
                    viewerRef.current.updateSensorValues(sensorValues);
                    console.log('GlbViewer: viewer.updateSensorValues returned');
                } catch (e) {
                    try { console.error('GlbViewer: updateSensorValues threw', e); } catch (ee) { }
                }
            } else if (hasLoad) {
                // Fallback: older viewer implementations may not expose updateSensorValues.
                // This fallback calls `viewer.load` with sensor data; keep as a compatibility
                // fallback but prefer the `updateSensorValues` path. Marked for deprecation
                // once all consumers provide updateSensorValues.
                try {
                    console.log('GlbViewer: falling back to viewer.load with sensorsData');
                    viewerRef.current.load({ sensorsData: sensors });
                    console.log('GlbViewer: viewer.load returned');
                } catch (e) {
                    try { console.error('GlbViewer: viewer.load threw', e); } catch (ee) { }
                }
            }
        } catch (e) { logger.warn && logger.warn('GlbViewer: sensorValues update failed', e); }
    }, [sensorValues]);

    // Camera persistence polling: if persistence requested and cameraState is a persisted prop, poll camera and setProps
    useEffect(() => {
        try {
            const shouldPersist = !!persistence && Array.isArray(persisted_props) && persisted_props.indexOf('cameraState') >= 0;
            if (!shouldPersist) return;
            // start polling when viewer ready; only send when state changed
            function poll() {
                try {
                    const v = viewerRef.current;
                    if (!v) return;
                    const cam = v._getCamera && v._getCamera();
                    if (!cam) return;
                    const pos = cam.position ? { x: cam.position.x, y: cam.position.y, z: cam.position.z } : null;
                    const quat = cam.quaternion ? { x: cam.quaternion.x, y: cam.quaternion.y, z: cam.quaternion.z, w: cam.quaternion.w } : null;
                    const state = { position: pos, quaternion: quat };
                    const prev = lastCameraStateRef.current;
                    const changed = !prev || JSON.stringify(prev) !== JSON.stringify(state);
                    if (changed) {
                        lastCameraStateRef.current = state;
                        try { if (typeof setProps === 'function') setProps({ cameraState: state }); } catch (e) { }
                    }
                } catch (e) { }
            }
            cameraPollRef.current = setInterval(poll, 500);
            return () => { if (cameraPollRef.current) { clearInterval(cameraPollRef.current); cameraPollRef.current = null; } };
        } catch (e) { }
    }, [persistence, persisted_props, viewerRef.current]);

    // Persist label toggles: listen for label:persistent and report a list of persisted uids
    useEffect(() => {
        try {
            if (!viewerRef.current) return;
            const parent = containerRef && containerRef.current ? containerRef.current : null;
            if (!parent) return;

            function _onLabelPersistent(ev) {
                try {
                    logger.debug && logger.debug('GlbViewer: _onLabelPersistent event', ev && ev.detail ? ev.detail : ev);
                    const detail = ev && ev.detail ? ev.detail : null;
                    if (!detail) return;
                    const uid = detail.sensorUid || detail.sensorId || null;
                    const persistent = !!detail.persistent;
                    if (!uid) return;
                    const set = persistentLabelsRef.current || new Set();
                    if (persistent) set.add(String(uid)); else set.delete(String(uid));
                    logger.debug && logger.debug('GlbViewer: persistentLabels updated locally', Array.from(set));
                    persistentLabelsRef.current = set;
                    if (typeof setProps === 'function') {
                        try { setProps({ persistentLabels: Array.from(set) }); } catch (e) { }
                    }
                    // Also persist to localStorage as a fallback so tab switches
                    // preserve label state even if the host didn't enable Dash persistence.
                    try {
                        if (typeof id === 'string' && typeof localStorage !== 'undefined') {
                            const key = `glb-viewer-persistentLabels-${id}`;
                            try { localStorage.setItem(key, JSON.stringify(Array.from(set))); } catch (e) { }
                        }
                    } catch (e) { /* ignore storage errors */ }
                } catch (err) { }
            }

            parent.addEventListener('label:persistent', _onLabelPersistent);
            return () => { try { parent.removeEventListener('label:persistent', _onLabelPersistent); } catch (e) { } };
        } catch (e) { }
    }, [viewerRef.current]);

    const style = { width: width || '100%', height: height || '400px', position: 'relative' };

    return (
        <div id={id} style={style} ref={containerRef} />
    );
}

// Note: default props are applied via function default parameters above.

GlbViewer.propTypes = {
    /**
     * The ID used to identify this component in Dash callbacks.
     */
    id: PropTypes.string,
    /** URL to a YAML or JSON sensors file (server‑served). */
    sensorsYamlUrl: PropTypes.string,
    /** sensors data parsed by Dash (array or { sensors: [...] , model: 'id' }) */
    sensors: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    /** Map of sensor client keys to runtime values and metadata. Example: { "10-1": { "value": 23.1, "unit": "°C", "label": "Temp" } } */
    sensorValues: PropTypes.object,
    /** Base URL where model files (GLB/.meta.json) are served from. Example: '/assets/models/' */
    modelsPath: PropTypes.string,
    /** Optional model identifier to load (without extension). Loads <modelsPath><modelId>.glb */
    modelId: PropTypes.string,
    /** width value (CSS) */
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** height value (CSS) */
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Additional viewer options passed to the underlying initViewer (object). */
    options: PropTypes.object,
    /** modelLoaded is set when a model successfully loads (model id) */
    modelLoaded: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** lastSensorClicked contains an identifier for the last clicked sensor (object|null) */
    lastSensorClicked: PropTypes.any,
    /** hoveredSensor contains a small payload for the currently hovered sensor (or null) */
    hoveredSensor: PropTypes.any,
    /** External trigger: increment this number/string to call `fitToScene()` from the host. */
    fitTrigger: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** Enable the interactive sensor editor UI (DEV/demo). */
    enableSensorEditor: PropTypes.bool,
    /** Persistence flags and persisted props */
    persistence: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.number]),
    persistence_type: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
    persisted_props: PropTypes.array,
    /** cameraState is optionally persisted via `persisted_props` */
    cameraState: PropTypes.object,
    /** persistentLabels: array of sensor UIDs that should be persisted (label shown persistent) */
    persistentLabels: PropTypes.array,

    /**
     * Dash-assigned callback that should be called to report property changes
     * to Dash, to make them available for callbacks.
     */
    setProps: PropTypes.func
};

export default GlbViewer;
